import { useMemo } from 'react';

import { Adjustment } from 'types';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { Dialog } from 'components/dialog';
import { Loader } from 'components/loader';
import { Button } from 'components/button';
import { formatPrice } from 'utils/helpers';
import { TitleLevel, ButtonColors, ExpensesType } from 'types/enums';
import { CreateBonusAdvanceDialog } from 'components/create-bonus-advance-dialog';

import * as Styles from './styles';
import { PaycheckDialogProps } from './types';
import { Card, AdjustmentCard } from './card';
import { boldTypes, minusTypes, getTransformedPaychecks } from './helpers';

const customTypeValues = [
  ExpensesType.Bonus,
  ExpensesType.Expense,
  ExpensesType.Reimbursement,
  ExpensesType.Advance,
  ExpensesType.Misc,
];

export const PaycheckDialog = ({
  data,
  onPay,
  onClose,
  employee,
  isLoading,
  selectedEvent,
  setSelectedIds,
}: PaycheckDialogProps) => {
  const selectedEventPaycheck = data?.find(
    (value) => value.event.id === selectedEvent?.id
  );

  const transformedPaychecks = useMemo(
    () =>
      selectedEvent && selectedEventPaycheck
        ? getTransformedPaychecks(selectedEventPaycheck)
        : [],
    [data, selectedEvent, selectedEventPaycheck]
  );

  const isPayrollsNegative = Number(selectedEventPaycheck?.totalPayout) <= 0;

  return (
    <Dialog
      showCloseButton
      onClose={onClose}
      open={!!selectedEvent}
      closeOnDocumentClick={false}
      title={`Paycheck ${selectedEvent?.name}`}
    >
      {() => (
        <Loader isFullScreen={false} isLoading={isLoading}>
          <Flex gap={8} fullWidth flexDirection="column">
            {transformedPaychecks.map(({ key, name, value }) => {
              const adjustmentTypes: Record<string, Adjustment[] | undefined> =
                {
                  refundTotal: selectedEventPaycheck?.refundAdjustments,
                  chargebackTotal: selectedEventPaycheck?.chargebackAdjustments,
                };

              if (Object.keys(adjustmentTypes).includes(key)) {
                return (
                  <AdjustmentCard
                    key={name}
                    name={name}
                    value={Number(value)}
                    adjustments={adjustmentTypes[key]}
                    currentEventName={selectedEvent?.name}
                  />
                );
              }

              return (
                <Card
                  key={name}
                  name={name}
                  value={Number(value)}
                  isRed={minusTypes.includes(key)}
                  titleLevel={
                    boldTypes.includes(key) ? TitleLevel.H4 : TitleLevel.P
                  }
                />
              );
            })}
            {!!employee && (
              <CreateBonusAdvanceDialog
                withReceipt
                isWideDialog
                isDisableEvent
                hideCashFromEvent
                customTitle="Add row"
                showSelectExpensesType
                employeeId={String(employee?.id)}
                eventId={Number(selectedEvent?.id)}
                customTypeValues={customTypeValues}
                customEmployeeOptions={[
                  {
                    value: String(employee?.id),
                    label: String(employee?.user?.name),
                  },
                ]}
                onSuccessSubmit={(values) =>
                  setSelectedIds?.((prevValues: number[]) => [
                    ...prevValues,
                    values.id,
                  ])
                }
                trigger={
                  <Styles.AddRowButton
                    transparent
                    padding="0"
                    text="Add row"
                    icon={<Icon.Plus />}
                    colorType={ButtonColors.LightBlue}
                  />
                }
              />
            )}

            {!!onPay && (
              <Button
                fullWidth
                onClick={onPay}
                disabled={isPayrollsNegative}
                text={`Pay (${formatPrice(selectedEventPaycheck?.totalPayout)})`}
              />
            )}
          </Flex>
        </Loader>
      )}
    </Dialog>
  );
};
